import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import useAxiosInstance from "../../hooks/useAxios";
import FormRender from "../../components/FormRender";
import JsonView from "@uiw/react-json-view";
import { API_USAGE_FORM_SETTINGS } from "../../helper.js";

const FILTER_KEYS = ["method", "status", "endpoint", "client_source"];

const SelectorModal = ({ title, onClose, children }) => {
  const overlayRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return createPortal(
    <div
      ref={overlayRef}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={(e) => { if (e.target === overlayRef.current) onClose(); }}
    >
      <div className="bg-white rounded-xl shadow-2xl w-[560px] h-[92vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <span className="text-sm font-semibold text-gray-800">{title}</span>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-lg leading-none"
          >
            ✕
          </button>
        </div>
        <div className="overflow-y-auto p-5 flex-1 [&_.text-sm.font-semibold.mt-4.mb-2]:hidden [&_.text-sm.font-semibold.mb-2]:hidden [&_.h-36]:!h-full [&_.overflow-y-auto.h-36]:flex-1">
          {children}
        </div>
      </div>
    </div>,
    document.body
  );
};

const APIUsage = () => {
  const axiosInstance = useAxiosInstance();

  const [selectedOrg, setSelectedOrg] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);

  const [orgModalOpen, setOrgModalOpen] = useState(false);
  const [userModalOpen, setUserModalOpen] = useState(false);

  const [apiRequests, setApiRequests] = useState([]);
  const [apiRequestsLoading, setApiRequestsLoading] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [requestResponse, setRequestResponse] = useState(null);
  const [activeTab, setActiveTab] = useState("request");

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalResults, setTotalResults] = useState(0);

  const [formData, setFormData] = useState({
    selected_org: {},
    selected_user: {},
    request_json: {},
    response_json: {}
  });

  const [filterFormData, setFilterFormData] = useState({
    method: "",
    status: "",
    endpoint: "",
    client_source: "",
    page_size: "50"
  });

  const formSettings = [
    { label: "Select Organisation*", type: "org", saveKey: "selected_org", single: true }
  ];

  const userFormSettings = [
    { label: "Select User*", type: "user", saveKey: "selected_user", single: true }
  ];

  const filterSettingsUserTable = {
    queryParams: "org_ids",
    changingValue: selectedOrg || ""
  };

  const endpointOptions = React.useMemo(() => {
    const endpoints = [...new Set(apiRequests.map(req => req.endpoint).filter(ep => typeof ep === "string"))];
    return [{ label: "All Endpoints", value: "" }, ...endpoints.map(ep => ({ label: ep, value: ep }))];
  }, [apiRequests]);

  const filterSettings = React.useMemo(() => {
    const base = API_USAGE_FORM_SETTINGS.filters.slice(0, 4);
    return base.map(f => f.saveKey === "endpoint" ? { ...f, options: endpointOptions } : f);
  }, [endpointOptions]);

  const activeFilterCount = FILTER_KEYS.filter(k => filterFormData[k]).length;

  // Derive selected org name for display
  const selectedOrgName = React.useMemo(() => {
    const id = Object.keys(formData.selected_org || {})[0];
    return id ? formData.selected_org[id]?.name : null;
  }, [formData.selected_org]);

  useEffect(() => {
    const selectedOrgId = Object.keys(formData.selected_org || {})[0];
    const orgId = selectedOrgId ? Number(selectedOrgId) : null;
    if (orgId !== selectedOrg) {
      setSelectedOrg(orgId);
      setSelectedUser(null);
      setFormData(prev => ({ ...prev, selected_user: {}, request_json: {}, response_json: {} }));
      setApiRequests([]);
      setSelectedRequest(null);
    }
  }, [formData.selected_org]);

  useEffect(() => {
    const selectedUserId = Object.keys(formData.selected_user || {})[0];
    if (selectedUserId) {
      setSelectedUser(formData.selected_user[selectedUserId]);
      setCurrentPage(1);
      setUserModalOpen(false);
    } else {
      setSelectedUser(null);
    }
  }, [formData.selected_user]);

  // Close org modal and open user modal once org is picked
  useEffect(() => {
    if (selectedOrg) {
      setOrgModalOpen(false);
    }
  }, [selectedOrg]);

  useEffect(() => {
    if (!selectedUser) {
      setApiRequests([]);
      setSelectedRequest(null);
      return;
    }

    setApiRequestsLoading(true);
    setSelectedRequest(null);

    const params = {
      user: selectedUser.username,
      page: currentPage,
      page_size: Number(filterFormData.page_size)
    };

    if (filterFormData.method) params.method = filterFormData.method;
    if (filterFormData.endpoint) params.endpoint = filterFormData.endpoint;
    if (filterFormData.status) params.status = filterFormData.status;
    if (filterFormData.client_source) params.client_source = filterFormData.client_source;

    axiosInstance
      .get(`/enterprise/usage-logs/`, { params })
      .then((res) => {
        setApiRequests(res.data?.results || []);
        setTotalResults(res.data?.total || 0);
        setTotalPages(res.data?.total_pages || 1);
        setApiRequestsLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching API usage logs:", err);
        setApiRequests([]);
        setApiRequestsLoading(false);
      });
  }, [selectedUser, currentPage, filterFormData]);

  useEffect(() => {
    if (!selectedRequest?.response_blob_path) {
      setRequestResponse(null);
      return;
    }

    fetch(selectedRequest.response_blob_path)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.blob();
      })
      .then((blob) => blob.text())
      .then((text) => {
        try {
          setRequestResponse(JSON.parse(text));
        } catch {
          setRequestResponse({ raw: text, parseError: "Failed to parse as JSON", rawContent: text });
        }
      })
      .catch((err) => {
        console.error("Error fetching response from blob:", err);
        setRequestResponse({ error: "Failed to load response", message: err.message });
      });
  }, [selectedRequest]);

  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      request_json: selectedRequest || {},
      response_json: requestResponse || {}
    }));
  }, [selectedRequest, requestResponse]);

  useEffect(() => {
    if (requestResponse) setActiveTab("response");
  }, [requestResponse]);

  useEffect(() => {
    if (selectedRequest) setActiveTab("request");
  }, [selectedRequest]);

  const getMethodColor = (method) => {
    switch (method?.toUpperCase()) {
      case "GET":    return "bg-green-100 text-green-700";
      case "POST":   return "bg-blue-100 text-blue-700";
      case "PUT":    return "bg-yellow-100 text-yellow-700";
      case "DELETE": return "bg-red-100 text-red-700";
      default:       return "bg-gray-100 text-gray-700";
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "success":              return "bg-green-100 text-green-700";
      case "failed":               return "bg-red-100 text-red-700";
      case "pending":              return "bg-yellow-100 text-yellow-700";
      case "insufficient_credits": return "bg-orange-100 text-orange-700";
      default:                     return "bg-gray-100 text-gray-700";
    }
  };

  const resetFilters = () => {
    setFilterFormData({ method: "", status: "", endpoint: "", client_source: "", page_size: "50" });
    setCurrentPage(1);
  };

  return (
    <div className="w-full h-full flex flex-col p-6 gap-4">
      <h1 className="text-xl font-semibold text-gray-900">API Usage</h1>

      <div className="flex gap-4 flex-1 min-h-0">

        {/* Left: Requests table */}
        <div className="flex flex-col border rounded-lg bg-white min-h-0 w-[45%]">
          {/* Header: selectors + filters */}
          <div className="px-4 pt-4 pb-3 border-b flex flex-col gap-3">

            {/* Org / User selector buttons */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setOrgModalOpen(true)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs transition-colors ${
                  selectedOrgName
                    ? "border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100"
                    : "border-gray-200 bg-white text-gray-500 hover:bg-gray-50"
                }`}
              >
                <span className="text-gray-400">Org:</span>
                <span className="font-medium truncate max-w-[120px]">
                  {selectedOrgName ?? "Select"}
                </span>
                <span className="text-gray-400 text-[10px]">▾</span>
              </button>

              <button
                onClick={() => setUserModalOpen(true)}
                disabled={!selectedOrg}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                  selectedUser
                    ? "border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100"
                    : "border-gray-200 bg-white text-gray-500 hover:bg-gray-50"
                }`}
              >
                <span className="text-gray-400">User:</span>
                <span className="font-medium truncate max-w-[120px]">
                  {selectedUser?.username ?? "Select"}
                </span>
                <span className="text-gray-400 text-[10px]">▾</span>
              </button>

              <div className="ml-auto flex items-center gap-2">
                {activeFilterCount > 0 && (
                  <button
                    onClick={resetFilters}
                    className="text-xs text-blue-600 hover:text-blue-800"
                  >
                    Clear filters ({activeFilterCount})
                  </button>
                )}
                {totalResults > 0 && (
                  <span className="text-xs text-gray-400">{totalResults} total</span>
                )}
              </div>
            </div>

            {/* Filter row */}
            <div className="grid grid-cols-4 gap-2">
              {filterSettings.map(f => (
                <select
                  key={f.saveKey}
                  value={filterFormData[f.saveKey]}
                  onChange={e => {
                    setFilterFormData(prev => ({ ...prev, [f.saveKey]: e.target.value }));
                    setCurrentPage(1);
                  }}
                  className="border rounded px-2 py-1.5 text-xs text-gray-700 bg-white focus:outline-none focus:ring-1 focus:ring-gray-300"
                >
                  {f.options?.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              ))}
            </div>
          </div>

          {/* Table body */}
          <div className="overflow-y-auto flex-1">
            {!selectedUser ? (
              <div className="flex flex-col items-center justify-center h-full text-center px-6">
                <p className="text-sm font-medium text-gray-400">Select an org and user to view requests</p>
                <button
                  onClick={() => setOrgModalOpen(true)}
                  className="mt-3 text-xs px-4 py-2 border rounded-lg hover:bg-gray-50 text-gray-600"
                >
                  Get started
                </button>
              </div>
            ) : apiRequestsLoading ? (
              <div className="flex items-center justify-center h-full">
                <p className="text-sm text-gray-400">Loading...</p>
              </div>
            ) : apiRequests.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full">
                <p className="text-sm text-gray-400">No requests found</p>
                {activeFilterCount > 0 && (
                  <button onClick={resetFilters} className="text-xs text-blue-600 mt-2 hover:underline">
                    Clear filters
                  </button>
                )}
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="border-b text-left bg-gray-50 sticky top-0 z-10">
                  <tr>
                    <th className="px-3 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wide">Method</th>
                    <th className="px-3 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wide">Endpoint</th>
                    <th className="px-3 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wide">Status</th>
                    <th className="px-3 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wide">Time</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {apiRequests.map((req) => (
                    <tr
                      key={req.id}
                      className={`cursor-pointer transition-colors ${
                        selectedRequest?.id === req.id
                          ? "bg-blue-50 border-l-2 border-l-blue-500"
                          : "hover:bg-gray-50"
                      }`}
                      onClick={() => setSelectedRequest(req)}
                    >
                      <td className="px-3 py-2.5">
                        <span className={`px-2 py-0.5 rounded text-xs font-semibold ${getMethodColor(req.method)}`}>
                          {req.method}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 text-gray-700 max-w-[180px]">
                        <span className="block truncate text-xs" title={req.endpoint}>{req.endpoint}</span>
                      </td>
                      <td className="px-3 py-2.5">
                        <span className={`px-2 py-0.5 rounded text-xs font-semibold ${getStatusColor(req.status)}`}>
                          {req.status}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 text-xs text-gray-400 tabular-nums">
                        {req.time_taken?.toFixed(2)}s
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Pagination footer */}
          {selectedUser && apiRequests.length > 0 && (
            <div className="flex items-center justify-between px-4 py-2.5 border-t bg-gray-50 rounded-b-lg">
              <span className="text-xs text-gray-400">
                {apiRequests.length} of {totalResults}
              </span>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-2.5 py-1 border rounded text-xs disabled:opacity-40 hover:bg-white transition-colors"
                >←</button>
                <span className="text-xs text-gray-500 px-2 tabular-nums">
                  {currentPage} / {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage >= totalPages}
                  className="px-2.5 py-1 border rounded text-xs disabled:opacity-40 hover:bg-white transition-colors"
                >→</button>
              </div>
            </div>
          )}
        </div>

        {/* Right: Request/Response detail panel */}
        <div className="flex flex-col border rounded-lg bg-white flex-1 min-h-0 min-w-0">
          {!selectedRequest ? (
            <div className="flex flex-col items-center justify-center h-full text-center px-6">
              <p className="text-sm font-medium text-gray-400">Select a request to inspect</p>
              <p className="text-xs text-gray-300 mt-1">Request and response JSON will appear here</p>
            </div>
          ) : (
            <>
              <div className="px-4 py-3 border-b flex items-center gap-3 flex-shrink-0">
                <span className={`px-2 py-0.5 rounded text-xs font-semibold ${getMethodColor(selectedRequest.method)}`}>
                  {selectedRequest.method}
                </span>
                <span className="text-sm text-gray-700 truncate flex-1 font-mono" title={selectedRequest.endpoint}>
                  {selectedRequest.endpoint}
                </span>
                <span className={`px-2 py-0.5 rounded text-xs font-semibold ${getStatusColor(selectedRequest.status)}`}>
                  {selectedRequest.status}
                </span>
                {selectedRequest.time_taken && (
                  <span className="text-xs text-gray-400 tabular-nums flex-shrink-0">
                    {selectedRequest.time_taken.toFixed(2)}s
                  </span>
                )}
              </div>

              <div className="flex border-b px-4 flex-shrink-0">
                {["request", "response"].map(tab => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors capitalize ${
                      activeTab === tab
                        ? "border-blue-500 text-blue-600"
                        : "border-transparent text-gray-500 hover:text-gray-700"
                    }`}
                  >
                    {tab}
                  </button>
                ))}
                {activeTab === "response" && selectedRequest?.response_blob_path && (
                  <a
                    href={selectedRequest.response_blob_path}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ml-auto self-center text-xs px-3 py-1 border rounded hover:bg-gray-50 text-gray-600"
                  >
                    Download
                  </a>
                )}
              </div>

              <div className="flex-1 overflow-auto p-4 min-h-0">
                <JsonView
                  shortenTextAfterLength={0}
                  value={activeTab === "request" ? (formData.request_json || {}) : (formData.response_json || {})}
                  displayObjectSize={false}
                  displayDataTypes={false}
                />
              </div>
            </>
          )}
        </div>
      </div>

      {/* Org selector modal */}
      {orgModalOpen && (
        <SelectorModal title="Select Organisation" onClose={() => setOrgModalOpen(false)}>
          <FormRender
            formData={formData}
            setFormData={setFormData}
            formSettings={formSettings}
          />
        </SelectorModal>
      )}

      {/* User selector modal */}
      {userModalOpen && (
        <SelectorModal title="Select User" onClose={() => setUserModalOpen(false)}>
          <FormRender
            formData={formData}
            setFormData={setFormData}
            formSettings={userFormSettings}
            filterSettingsUserTable={filterSettingsUserTable}
          />
        </SelectorModal>
      )}
    </div>
  );
};

export default APIUsage;
